/// <amd-module name="Build/Common/Library/Components/BuildPersona" />
import * as React from "react";
import * as Resources from "Build/Common/Library/Resources";
import { VssPersona, VssPersonaSize, IIdentityDetailsProvider } from "VSSUI/VssPersona";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

export interface IBuildPersonaProps {
    cssClass?: string;

    /** Size of image to display. Use VssPersonaSize presets. */
    size?: VssPersonaSize;

    /**
     * The user's displayname.
     */
    displayName: string | undefined;

    /**
     * A URL to a profile image.
     */
    imageUrl: string | undefined;

    /**
     * Optional shared collection of imageUrls that can't be loaded.
     * Used to avoid redundant image load attempts across BuildPersona instances.
     */
    unloadableImageUrls?: Set<string>;
}

export interface IBuildPersonaState {
    // We used to have an imageError boolean property here, but it was too "sticky"; props.imageUrl can change as we
    // process SignalR buildUpdated events.
    unloadableImageUrls: Set<string>;
}

export class BuildPersona extends VssComponent<IBuildPersonaProps, IBuildPersonaState> {
    constructor(props: IBuildPersonaProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            unloadableImageUrls: props.unloadableImageUrls || new Set<string>()
        };
    }

    public render() {
        return (<VssPersona
            size={this.props.size}
            cssClass={this.props.cssClass}
            identityDetailsProvider={{
                getIdentityImageUrl: (size: number): string | undefined => {
                    if (!this.props.imageUrl || this.state.unloadableImageUrls.has(this.props.imageUrl)) {
                        return this._getFallbackImageUrl(size);
                    }
                    return this.props.imageUrl;
                },
                getDisplayName: (): string => {
                    if (!this.props.displayName) {
                        // For the aria-label.
                        return Resources.UnresolvedUser;
                    }
                    return this.props.displayName;
                }
            }}
          onImageError={this._onImageError}
        />);
    }

    private _getFallbackImageUrl = (size: number): string | undefined => {
        // If we have a displayname, return undefined so VssPersona displays an avatar based on the displayname initials.
        if (this.props.displayName) {
            return undefined;
        }

        // Otherwise, fallback to the default user avatar. In practice we use this for builds associated with a service
        // identity ("Microsoft.TeamFoundation.Services.TFS") and no SourceVersionInfo.
        return size >= 40
            ? getAssetLocation(this.context.pageContext, "ms.vss-build-web/common-library/default-user-avatar-large.svg")
            : getAssetLocation(this.context.pageContext, "ms.vss-build-web/common-library/default-user-avatar-small.svg");
    }

    private _onImageError = (): void => {
        if (this.props.imageUrl) {
            this.setState({
                unloadableImageUrls: this.state.unloadableImageUrls.add(this.props.imageUrl)
            });
        }
    }
}
